import { useMemo, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from '@emotion/styled/macro';
import { Flex, Heading, Tabs, TabList, TabPanels, Tab, TabPanel } from '@chakra-ui/react';

import { getValidRowsWithDates } from '../../services/grid';
import {
    getRenewableDataChart,
    createRenewableData,
    getRenewableData,
    updateRenewableData,
    deleteRenewableData,
    deleteAllRenewableData,
    formatParsedNewRenewableData,
    getRenewableForecastChart,
    createRenewableForecasts,
    getRenewableForecast,
    updateRenewableForecast,
    deleteRenewableForecasts,
    deleteAllRenewableForecasts,
    formatParsedNewRenewableForecast,
    createRenewableCosts,
    getRenewableCosts,
    updateRenewableCosts,
    deleteRenewableCosts,
    deleteAllRenewableCosts,
    formatParsedNewRenewableCosts,
    createRenewableAncillaries,
    getRenewableAncillaries,
    updateRenewableAncillaries,
    deleteRenewableAncillaries,
    deleteAllRenewableAncillaries,
    formatParsedNewRenewableAncillaries,
    createRenewableModels,
    getRenewableModels,
    updateRenewableModels,
    deleteRenewableModels,
    deleteAllRenewableModels,
    formatParsedNewRenewableModels,
} from '../../services/renewables';

import SubItemData from '../itemData/SubItemData';
import DownloadButton from './../itemData/DownloadButton';
import UploadButton from './../itemData/UploadButton';

import Combobox from '../forms/Combobox';

import SecondaryButton from '../utils/SecondaryButton';

import { ReactComponent as RefreshIcon } from '../../icons/refresh.svg';

const SubRenewableData = ({ itemLabel, subRenewableId, itemId, itemType, filters }) => {
    const forecastTypes = useSelector((state) => state.forecast.types);
    const intl = useIntl();

    const [key, setKey] = useState(0);
    const [loadingQueue, setLoadingQueue] = useState([]); // unique keys represeting which resource is in loading state
    const [forecastType, setForecastType] = useState(122);

    const refreshTabs = useCallback(() => setKey((prev) => prev + 1), []);

    const onLoadingStart = useCallback((key) => {
        setLoadingQueue((prev) => prev.concat(key));
    }, []);

    const onLoadingEnd = useCallback((key) => {
        setLoadingQueue((prev) => prev.filter((p) => p !== key));
    }, []);

    const renewableDataChartResource = useMemo(() => {
        return {
            read: (pagination) => {
                const payload = {
                    pointCount: pagination.pointCount,
                    startDateFrom: pagination.startDateFrom || filters.startDateFrom,
                    startDateTo: pagination.startDateTo || filters.startDateTo,
                    updateDateFrom: filters.updateDateFrom,
                    updateDateTo: filters.updateDateTo,
                    filterType: filters.filterType,
                };

                return getRenewableDataChart(subRenewableId, payload);
            },
        };
    }, [subRenewableId, filters]);

    const renewableDataGridResource = useMemo(() => {
        return {
            create: (rows) => createRenewableData(subRenewableId, rows),
            read: (pagination) => getRenewableData(subRenewableId, { ...pagination, ...filters }),
            update: (rows) => {
                const validRows = getValidRowsWithDates(rows, ['startDate', 'endDate', 'updateDateTime']);

                return updateRenewableData(subRenewableId, validRows);
            },
            delete: (rows) =>
                deleteRenewableData(
                    subRenewableId,
                    rows.map((row) => row.windDataId)
                ),
            deleteAll: () => deleteAllRenewableData(subRenewableId),
        };
    }, [subRenewableId, filters]);

    const renewableDataColumnSchema = useMemo(() => {
        return {
            startDate: { name: 'startDate', type: 'date' },
            endDate: { name: 'endDate', type: 'date' },
            windOrSolar: { name: 'windOrSolar', type: 'number' },
            updateDateTime: { name: 'updateDateTime', type: 'date' },
        };
    }, []);

    const renewableDataUnitLabel = intl.formatMessage(
        { id: 'common_item_unit_label' },
        { item: itemLabel.charAt(0).toUpperCase() + itemLabel.slice(1), unit: 'MWh' }
    );

    const renewableForecastChartResource = useMemo(() => {
        return {
            read: (pagination) => {
                const payload = {
                    pointCount: pagination.pointCount,
                    startDateFrom: pagination.startDateFrom || filters.startDateFrom,
                    startDateTo: pagination.startDateTo || filters.startDateTo,
                    updateDateFrom: filters.updateDateFrom,
                    updateDateTo: filters.updateDateTo,
                    filterType: filters.filterType,
                    forecastTypeId: forecastType,
                };

                return getRenewableForecastChart(subRenewableId, payload);
            },
        };
    }, [subRenewableId, filters, forecastType]);

    const renewableForecastGridResource = useMemo(() => {
        return {
            create: (rows) => createRenewableForecasts(subRenewableId, rows),
            read: (pagination) => getRenewableForecast(subRenewableId, { ...pagination, ...filters }),
            update: (rows) => {
                const validRows = getValidRowsWithDates(rows, ['startDate', 'endDate', 'updateDateTime']);

                return updateRenewableForecast(subRenewableId, validRows);
            },
            delete: (rows) =>
                deleteRenewableForecasts(
                    subRenewableId,
                    rows.map((row) => row.windForecastId)
                ),
            deleteAll: () => deleteAllRenewableForecasts(subRenewableId),
        };
    }, [subRenewableId, filters]);

    const loadForecastColumnSchema = useMemo(() => {
        return {
            forecastTypeId: {
                name: 'forecastTypeId',
                type: 'string',
                isRequired: true,
                constraints: forecastTypes.map((type) => type.description),
            },
            startDate: { name: 'startDate', type: 'date' },
            endDate: { name: 'endDate', type: 'date' },
            expectedEnergy: { name: 'expectedEnergy', type: 'number' },
            expectedPeakGeneration: { name: 'expectedPeakGeneration', type: 'number' },
            capacity: { name: 'capacity', type: 'number' },
            updateDateTime: { name: 'updateDateTime', type: 'date' },
        };
    }, [forecastTypes]);

    const formatNewRenewableForecast = useCallback(
        (data) => formatParsedNewRenewableForecast(data, forecastTypes),
        [forecastTypes]
    );

    const renewableCostsGridResource = useMemo(() => {
        return {
            create: (rows) => createRenewableCosts(subRenewableId, rows),
            read: () => getRenewableCosts(subRenewableId),
            update: (rows) => {
                const validRows = getValidRowsWithDates(rows, ['startDate', 'endDate', 'updateDateTime']);

                return updateRenewableCosts(subRenewableId, validRows);
            },
            delete: (rows) =>
                deleteRenewableCosts(
                    subRenewableId,
                    rows.map((row) => row.windIdCostId)
                ),
            deleteAll: () => deleteAllRenewableCosts(subRenewableId),
        };
    }, [subRenewableId]);

    const renewableCostsColumnSchema = useMemo(() => {
        return {
            startDate: { name: 'startDate', type: 'date' },
            endDate: { name: 'endDate', type: 'date' },
            fixedCost: { name: 'fixedCost', type: 'number' },
            vom: { name: 'vom', type: 'number' },
            curtailPrice: { name: 'curtailPrice', type: 'number' },
            curtailPercentage: { name: 'curtailPercentage', type: 'number' },
            updateDateTime: { name: 'updateDateTime', type: 'date' },
        };
    }, []);

    const renewableAncillariesGridResource = useMemo(() => {
        return {
            create: (rows) => createRenewableAncillaries(subRenewableId, rows),
            read: () => getRenewableAncillaries(subRenewableId),
            update: (rows) => {
                const validRows = getValidRowsWithDates(rows, ['startDate', 'endDate']);

                return updateRenewableAncillaries(subRenewableId, validRows);
            },
            delete: (rows) =>
                deleteRenewableAncillaries(
                    subRenewableId,
                    rows.map((row) => row.windAncillaryId)
                ),
            deleteAll: () => deleteAllRenewableAncillaries(subRenewableId),
        };
    }, [subRenewableId]);

    const renewableAncillariesColumnSchema = useMemo(() => {
        return {
            startDate: { name: 'startDate', type: 'date' },
            endDate: { name: 'endDate', type: 'date' },
            canContributeContingent: { name: 'canContributeContingent', isRequired: true, type: 'bool-number' },
        };
    }, []);

    const renewableModelsGridResource = useMemo(() => {
        return {
            create: (rows) => createRenewableModels(subRenewableId, rows),
            read: () => getRenewableModels(subRenewableId),
            update: (rows) => updateRenewableModels(subRenewableId, rows),
            delete: (rows) =>
                deleteRenewableModels(
                    subRenewableId,
                    rows.map((row) => row.windModelId)
                ),
            deleteAll: () => deleteAllRenewableModels(subRenewableId),
        };
    }, [subRenewableId]);

    const renewableModelsColumnSchema = useMemo(() => {
        return {
            useFlag: { name: 'useFlag', isRequired: true, type: 'bool-number' },
            description: { name: 'description', type: 'string' },
            formulaStatement: { name: 'formulaStatement', type: 'string' },
            runOrder: { name: 'runOrder', type: 'number' },
        };
    }, []);

    return (
        <Tabs px={0} mt={6} isLazy isManual lazyBehavior="keepMounted" key={key}>
            <StyledTabList w="100%" flexWrap={{ base: 'wrap', xl: 'nowrap' }} alignItems="center">
                <StyledTab>
                    <FormattedMessage id="common_historical_data_data" />
                </StyledTab>

                <StyledTab>
                    <FormattedMessage id="common_historical_data_forecast" />
                </StyledTab>

                <StyledTab>
                    <FormattedMessage id="common_historical_data_costs" />
                </StyledTab>

                <StyledTab>
                    <FormattedMessage id="common_historical_data_ancillaries" />
                </StyledTab>

                <StyledTab>
                    <FormattedMessage id="common_historical_data_model" />
                </StyledTab>

                <Flex ml="auto" order={{ base: -1, xl: 0 }} w={{ base: '100%', xl: 'auto' }}>
                    <DownloadButton
                        type="renewables"
                        itemId={itemId}
                        filters={filters}
                        hasLabel={true}
                        itemType={itemType}
                        mr={2}
                        w="auto !important"
                    />

                    <UploadButton type="renewables" itemId={itemId} hasLabel={true} mr={4} w="auto !important" />

                    <SecondaryButton
                        onClick={refreshTabs}
                        size="sm"
                        variant="secondary"
                        leftIcon={<RefreshIcon />}
                        isLoading={loadingQueue.length > 0}
                        loadingText={intl.formatMessage({ id: 'common_loading' })}
                        textTransform="capitalize"
                        ml="auto"
                    >
                        <FormattedMessage id="common_refresh" />
                    </SecondaryButton>
                </Flex>
            </StyledTabList>

            <TabPanels mt={4}>
                <TabPanel>
                    <Heading as="h3" variant="h3" textTransform="capitalize">
                        <FormattedMessage id="common_historical_data_heading" values={{ item: itemLabel }} />
                    </Heading>

                    <SubItemData
                        hasChart
                        tab="0"
                        yAxisLabel={renewableDataUnitLabel}
                        chartResource={renewableDataChartResource}
                        onLoadingStart={onLoadingStart}
                        onLoadingEnd={onLoadingEnd}
                        gridResource={renewableDataGridResource}
                        columns={[
                            {
                                type: 'date',
                                field: 'startDate',
                                headerName: intl.formatMessage({ id: 'common_grid_start_date' }),
                                sortable: true,
                                sort: 'asc',
                            },
                            {
                                type: 'date',
                                field: 'endDate',
                                headerName: intl.formatMessage({ id: 'common_grid_end_date' }),
                            },
                            {
                                field: 'windOrSolar',
                                type: 'number',
                                headerName: renewableDataUnitLabel,
                            },
                            {
                                type: 'date',
                                field: 'updateDateTime',
                                headerName: intl.formatMessage({ id: 'common_grid_created' }),
                                sortable: true,
                                sort: 'asc',
                            },
                        ]}
                        addRowsValidationSchema={renewableDataColumnSchema}
                        formatNewRows={formatParsedNewRenewableData}
                    />
                </TabPanel>

                <TabPanel>
                    <Heading as="h3" variant="h3" textTransform="capitalize">
                        <FormattedMessage id="common_historical_data_forecast_heading" values={{ item: itemLabel }} />
                    </Heading>

                    <SubItemData
                        hasChart
                        actionBarSlot={(index) =>
                            index === 0 ? (
                                <Combobox
                                    width="var(--chakra-sizes-2xs) !important"
                                    options={forecastTypes}
                                    valueKey="id"
                                    labelKey="description"
                                    isControlled={false}
                                    onChange={setForecastType}
                                />
                            ) : null
                        }
                        tab="1"
                        yAxisLabel={renewableDataUnitLabel}
                        chartResource={renewableForecastChartResource}
                        onLoadingStart={onLoadingStart}
                        onLoadingEnd={onLoadingEnd}
                        gridResource={renewableForecastGridResource}
                        columns={[
                            {
                                field: 'forecastTypeId',
                                type: 'select',
                                cellEditorParams: { options: forecastTypes },
                                headerName: intl.formatMessage({ id: 'common_grid_forecast_type' }),
                            },
                            {
                                type: 'date',
                                field: 'startDate',
                                headerName: intl.formatMessage({ id: 'common_grid_start_date' }),
                                sortable: true,
                                sort: 'asc',
                            },
                            {
                                type: 'date',
                                field: 'endDate',
                                headerName: intl.formatMessage({ id: 'common_grid_end_date' }),
                            },
                            {
                                field: 'expectedEnergy',
                                type: 'number',
                                headerName: intl.formatMessage(
                                    { id: 'common_item_unit_label' },
                                    { item: 'Expected Energy', unit: 'MWh' }
                                ),
                            },
                            {
                                field: 'expectedPeakGeneration',
                                type: 'number',
                                headerName: intl.formatMessage(
                                    { id: 'common_item_unit_label' },
                                    { item: 'Expected Peak Generation', unit: 'MW' }
                                ),
                            },
                            {
                                field: 'capacity',
                                type: 'number',
                                headerName: intl.formatMessage(
                                    { id: 'common_item_unit_label' },
                                    { item: 'Capacity', unit: 'MW' }
                                ),
                            },
                            {
                                type: 'date',
                                field: 'updateDateTime',
                                headerName: intl.formatMessage({ id: 'common_grid_created' }),
                                sortable: true,
                                sort: 'asc',
                            },
                        ]}
                        addRowsValidationSchema={loadForecastColumnSchema}
                        formatNewRows={formatNewRenewableForecast}
                    />
                </TabPanel>

                <TabPanel>
                    <Heading as="h3" variant="h3" textTransform="capitalize">
                        <FormattedMessage id="common_historical_data_costs_heading" values={{ item: itemLabel }} />
                    </Heading>

                    <SubItemData
                        tab="2"
                        onLoadingStart={onLoadingStart}
                        onLoadingEnd={onLoadingEnd}
                        gridResource={renewableCostsGridResource}
                        columns={[
                            {
                                type: 'date',
                                field: 'startDate',
                                headerName: intl.formatMessage({ id: 'common_grid_start_date' }),
                                sortable: true,
                                sort: 'asc',
                            },
                            {
                                type: 'date',
                                field: 'endDate',
                                headerName: intl.formatMessage({ id: 'common_grid_end_date' }),
                            },
                            {
                                field: 'fixedCost',
                                type: 'number',
                                headerName: intl.formatMessage(
                                    { id: 'common_item_unit_label' },
                                    { item: 'Fixed Cost', unit: '$/month' }
                                ),
                            },
                            {
                                field: 'vom',
                                type: 'number',
                                headerName: intl.formatMessage(
                                    { id: 'common_item_unit_label' },
                                    { item: 'VOM', unit: '$/month' }
                                ),
                            },
                            {
                                field: 'curtailPrice',
                                type: 'number',
                                headerName: intl.formatMessage({ id: 'common_grid_curtailment_price' }),
                            },
                            {
                                field: 'curtailPercentage',
                                type: 'number',
                                headerName: intl.formatMessage(
                                    { id: 'common_item_unit_label' },
                                    { item: 'Curtailment Percentage', unit: '%, 0-100' }
                                ),
                            },
                            {
                                type: 'date',
                                field: 'updateDateTime',
                                headerName: intl.formatMessage({ id: 'common_grid_created' }),
                                sortable: true,
                                sort: 'asc',
                            },
                        ]}
                        addRowsValidationSchema={renewableCostsColumnSchema}
                        formatNewRows={formatParsedNewRenewableCosts}
                    />
                </TabPanel>

                <TabPanel>
                    <Heading as="h3" variant="h3" textTransform="capitalize">
                        <FormattedMessage
                            id="common_historical_data_ancillaries_heading"
                            values={{ item: itemLabel }}
                        />
                    </Heading>

                    <SubItemData
                        tab="3"
                        onLoadingStart={onLoadingStart}
                        onLoadingEnd={onLoadingEnd}
                        gridResource={renewableAncillariesGridResource}
                        columns={[
                            {
                                type: 'date',
                                field: 'startDate',
                                headerName: intl.formatMessage({ id: 'common_grid_start_date' }),
                                sortable: true,
                                sort: 'asc',
                            },
                            {
                                type: 'date',
                                field: 'endDate',
                                headerName: intl.formatMessage({ id: 'common_grid_end_date' }),
                            },
                            {
                                type: 'checkbox',
                                field: 'canContributeContingent',
                                headerName: intl.formatMessage({ id: 'common_grid_can_contribute_contingent' }),
                            },
                        ]}
                        addRowsValidationSchema={renewableAncillariesColumnSchema}
                        formatNewRows={formatParsedNewRenewableAncillaries}
                    />
                </TabPanel>

                <TabPanel>
                    <Heading as="h3" variant="h3" textTransform="capitalize">
                        <FormattedMessage id="common_historical_data_model_heading" values={{ item: itemLabel }} />
                    </Heading>

                    <SubItemData
                        tab="4"
                        onLoadingStart={onLoadingStart}
                        onLoadingEnd={onLoadingEnd}
                        gridResource={renewableModelsGridResource}
                        columns={[
                            {
                                type: 'checkbox',
                                field: 'useFlag',
                                headerName: intl.formatMessage({ id: 'common_grid_use' }),
                            },
                            {
                                field: 'description',
                                headerName: intl.formatMessage({ id: 'common_grid_model_description' }),
                            },
                            {
                                field: 'formulaStatement',
                                headerName: intl.formatMessage({ id: 'common_grid_formula_statement' }),
                            },
                            {
                                field: 'runOrder',
                                type: 'number',
                                headerName: intl.formatMessage({ id: 'common_grid_run_order' }),
                            },
                        ]}
                        addRowsValidationSchema={renewableModelsColumnSchema}
                        formatNewRows={formatParsedNewRenewableModels}
                    />
                </TabPanel>
            </TabPanels>
        </Tabs>
    );
};

// @todo duplicate styles in SubLoadData/SubRenewableData/SubBatteryData
const StyledTabList = styled(TabList)`
    border-bottom: 1px solid var(--chakra-colors-gray-200);

    > [aria-selected='true'] {
        border-bottom: 4px solid var(--chakra-colors-blue-400);
        color: var(--chakra-colors-black);
    }

    > [role='tab'] {
        color: var(--chakra-colors-black);

        &:hover {
            color: var(--chakra-colors-blue-400);
        }
    }
`;

const StyledTab = styled(Tab)`
    padding: 16px 0;
    margin-right: 24px;
    margin-bottom: 0;
    color: var(--chakra-colors-gray-600);
    text-transform: capitalize;
`;

export default SubRenewableData;
